import { GraphIOTypes } from '@agilelab/plugin-wb-marketplace-common';
import { useQuery } from '@apollo/client';
import { ErrorPanel, Progress } from '@mainstage/core-components';
import {
  createStyles,
  Grid,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core';
import { DateTime } from 'luxon';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

import {
  useEnvironmentsContext,
  WbCard,
  WbCardContent,
  WbInfoTooltip,
} from '@agilelab/plugin-wb-platform';
import { useTaxonomySelection } from '@agilelab/plugin-wb-practice-shaper';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import pluralize from 'pluralize';
import { ALIGNMENT_CHART_DATA, AlignmentByMonth } from '../../graphql';
import { extractTaxonomyUrn, getMonthsBetweenDateAndNow } from '../../utils';
import { ChartSrOnlySummary } from './ChartSrOnlySummary';
import { buildAlignmentSummary } from './chartAccessibility.utils';
import { CustomStatus } from '../CustomStatus/CustomStatus';
import { buildDashboardWhereQuery, getCardLabel } from './utils';

function parseData(data: {
  instances: {
    published_at: string;
    inputPorts: { data: { kind: GraphIOTypes } }[];
  }[];
}): AlignmentByMonth[] {
  if (!data.instances || data.instances.length === 0) {
    return [];
  }

  const dpInstances = [...data.instances].sort((a: any, b: any) =>
    b.published_at < a.published_at ? 1 : -1,
  );

  const alignmentsByMonth = dpInstances
    .flatMap(dpInstance =>
      dpInstance.inputPorts.reduce(
        (acc: AlignmentByMonth, obj: { data: { kind: GraphIOTypes } }) => {
          if (obj.data.kind === 'resource') {
            acc.totalSources += 1;
          } else {
            acc.totalConsumers += 1;
          }
          return acc;
        },
        {
          monthOfYear: DateTime.fromISO(dpInstance.published_at).toFormat(
            'MMM yyyy',
          ),
          totalSources: 0,
          totalConsumers: 0,
        },
      ),
    ) // group by month of year
    .reduce((acc: any, obj: AlignmentByMonth) => {
      const key = obj.monthOfYear;
      if (!acc[key]) {
        acc[key] = {
          totalSources: obj.totalSources,
          totalConsumers: obj.totalConsumers,
        };
        return acc;
      }

      acc[key] = {
        totalSources: acc[key].totalSources + obj.totalSources,
        totalConsumers: acc[key].totalConsumers + obj.totalConsumers,
      };
      return acc;
    }, {});

  const startDate = dpInstances.at(0)?.published_at || undefined;
  if (!startDate) {
    return [];
  }

  return getMonthsBetweenDateAndNow(startDate).map((monthOfYear: string) => {
    if (alignmentsByMonth[monthOfYear]) {
      return {
        monthOfYear: monthOfYear,
        totalConsumers: alignmentsByMonth[monthOfYear].totalConsumers,
        totalSources: alignmentsByMonth[monthOfYear].totalSources,
      } as AlignmentByMonth;
    }
    return {
      monthOfYear: monthOfYear,
      totalConsumers: 0,
      totalSources: 0,
    };
  });
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chartContainer: {
      position: 'relative',
      width: '100%',
      height: '320px',
    },
    alignmentsChartContainerEmpty: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -100%)',
      padding: '12px 16px',
      borderRadius: '4px',
      background: theme.palette.grey[300],
      color: 'black',
    },
  }),
);

const CustomTooltip = ({
  active,
  payload,
  label: _,
}: TooltipProps<ValueType, NameType>): JSX.Element => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <p
          style={{
            color: theme.palette.text.primary,
            padding: theme.spacing(0, 1),
          }}
        >{`${payload[0].payload.monthOfYear}`}</p>
        <p
          style={{
            color: theme.palette.primary.main,
            padding: theme.spacing(0, 1),
          }}
        >{`Consumer Aligned : ${payload[0].payload.totalConsumers}`}</p>
        <p
          style={{
            color: theme.palette.primary.light,
            padding: theme.spacing(0, 1),
          }}
        >{`Source Aligned : ${payload[0].payload.totalSources}`}</p>
      </div>
    );
  }

  return <></>;
};

export const EnvAlignmentKpiCard = (props: { animationsDuration?: number }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { selectedTaxonomyRef, systemTypes } = useTaxonomySelection();
  const { priorityEnvironment } = useEnvironmentsContext();
  const { animationsDuration } = props;
  const { loading, error, data } = useQuery(ALIGNMENT_CHART_DATA, {
    variables: {
      where: buildDashboardWhereQuery(extractTaxonomyUrn(selectedTaxonomyRef), [
        { environment: { name: { _eq: priorityEnvironment.name } } },
        {
          relations: {
            _or: [
              {
                _and: [
                  { name: { _eq: 'readsFrom' } },
                  {
                    instanceByTargetInstanceId: { kind: { _eq: 'component' } },
                  },
                ],
              },
              {
                _and: [
                  { name: { _eq: 'logicallyReadsFrom' } },
                  {
                    _or: [
                      {
                        instanceByTargetInstanceId: {
                          kind: { _eq: 'component' },
                        },
                      },
                      {
                        instanceByTargetInstanceId: { kind: { _eq: 'system' } },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        },
      ]),

      whereRelations: {
        _or: [
          {
            name: {
              _eq: 'readsFrom',
            },
          },
          {
            name: {
              _eq: 'logicallyReadsFrom',
            },
          },
        ],
      },
    },
  });

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <ErrorPanel error={error} />;
  }

  const hasData = data.instances && data.instances.length > 0;
  const alignmentData = hasData ? parseData(data) : [];

  const barColors = theme.palette.charts;
  const systemTypeLabel = pluralize(getCardLabel(systemTypes));
  const chartSummary = buildAlignmentSummary({
    chartName: `${systemTypeLabel} types chart`,
    data: alignmentData.map(point => ({
      label: point.monthOfYear,
      consumers: point.totalConsumers,
      sources: point.totalSources,
    })),
    consumersLabel: 'consumer aligned',
    sourcesLabel: 'source aligned',
  });
  const chartSummaryId = 'environment-alignment-summary';

  return (
    <WbCard
      title={`${systemTypeLabel} Types`}
      icon={
        <WbInfoTooltip
          title={`Ratio between source aligned ${systemTypeLabel} and consumer aligned ${systemTypeLabel}`}
          icon={<InfoOutlinedIcon color="primary" />}
        />
      }
      cardStyle={{ height: '100%' }}
    >
      <WbCardContent>
        <ChartSrOnlySummary summary={chartSummary} id={chartSummaryId} />
        <Grid container direction="row" justifyContent="flex-end">
          <Grid item>
            <CustomStatus getColor={() => barColors[0]}>
              Consumer Aligned
            </CustomStatus>
          </Grid>
          <Grid item>
            <CustomStatus getColor={() => barColors[1]}>
              Source Aligned
            </CustomStatus>
          </Grid>
        </Grid>
        <div className={classes.chartContainer}>
          {!hasData && (
            <div className={classes.alignmentsChartContainerEmpty}>
              No data available
            </div>
          )}
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={alignmentData}
              accessibilityLayer
              aria-label="Bar chart showing monthly consumer and source alignment"
              aria-describedby={chartSummaryId}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthOfYear" />
              <YAxis />
              <ChartTooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Bar
                dataKey="totalConsumers"
                stackId="a"
                fill={barColors[0]}
                animationDuration={animationsDuration}
              />
              <Bar
                dataKey="totalSources"
                stackId="a"
                fill={barColors[1]}
                animationDuration={animationsDuration}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </WbCardContent>
    </WbCard>
  );
};
